/**
 * Overview Dashboard - Multi-Retailer Landing Page
 * 
 * Shows aggregated performance across all retailers with breakdown.
 * Users can click retailer cards to drill down into detail views.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery } from '@tanstack/react-query';
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext';
import { useTenant } from '../contexts/TenantContext';
import { useCalendarWeeks } from '../lib/data-service';
import { apiClient } from '../lib/api-client';
import { exportOverviewToCSV } from '../lib/export-utils';
import { useKpiCardOrder, KpiCardType } from '../hooks/useKpiCardOrder';
import GlobalHeader from '../components/GlobalHeader';
import { CalendarWeekPicker } from '../components/CalendarWeekPicker';
import { OverviewKpiCard } from '../components/OverviewKpiCard';
import { OverviewSidebar } from '../components/OverviewSidebar';
import { RetailerCard } from '../components/RetailerCard';
import { RetailerSelector } from '../components/RetailerSelector';
import { PerformanceHighlights } from '../components/PerformanceHighlights';
import { RetailerGrowthTrends } from '../components/RetailerGrowthTrends';
import { AnomalyAlerts } from '../components/AnomalyAlerts';
import { DtcOverviewSection } from '../components/DtcOverviewSection';
import { OverviewKpiCardSkeleton } from '../components/skeletons/OverviewKpiCardSkeleton';
import { RetailerCardSkeleton } from '../components/skeletons/RetailerCardSkeleton';
import { NoMatchesEmpty, NoRetailersEmpty } from '../components/EmptyStates';
import { getAllAnomalies } from '../lib/anomaly-detection';
import AIInsightsPanel from '../components/AIInsightsPanel';
import { CollapsibleAIInsightsBar } from '../components/CollapsibleAIInsightsBar';
import { 
  DollarSign, 
  ShoppingCart, 
  Package, 
  Store,
  TrendingUp,
  Building2,
  Sparkles,
  BarChart3,
  Target,
  Download,
  Filter,
  ArrowUpDown,
  RotateCcw,
  GripVertical,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function OverviewDashboard() {
  const { selectedDateRange, setSelectedDateRange, setSelectedFiscalWeek } = useFiscalWeekContext();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [retailerFilter, setRetailerFilter] = useState<'all' | 'active' | 'pending'>('all');
  const [retailerSort, setRetailerSort] = useState<'revenue' | 'units' | 'skus' | 'alphabetical'>('revenue');
  const [showRetailSection, setShowRetailSection] = useState(true); // Retail dashboard section
  const [showDtcSection, setShowDtcSection] = useState(true); // DTC dashboard section
  
  // KPI card order management
  const { order: kpiCardOrder, updateOrder: updateKpiCardOrder, resetOrder: resetKpiCardOrder, isCustomOrder } = useKpiCardOrder();
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );
  
  // Calendar weeks used for export labels and trend lookups
  const { data: calendarData } = useCalendarWeeks();
  const calendarWeeks = calendarData?.weeks;

  // Auto-select latest completed week if no date range is set
  // (OverviewDashboard doesn't render GlobalHeader which normally handles this)
  useEffect(() => {
    if (!selectedDateRange && calendarData && calendarData.weeks.length > 0) {
      const current = calendarData.weeks.find(w => w.fiscal_week === calendarData.currentWeek && w.fiscal_year === calendarData.currentFiscalYear);
      const week = current ?? calendarData.weeks[0];
      setSelectedDateRange({ start: week.start_date, end: week.end_date });
      const fw = parseInt(week.fiscal_year.replace('FY', '')) * 100 + week.fiscal_week;
      setSelectedFiscalWeek(fw);
    }
  }, [calendarData, selectedDateRange, setSelectedDateRange, setSelectedFiscalWeek]);
  
  // Get aggregated KPIs (all retailers combined) using date range
  const { data: kpis, isLoading: kpisLoading, refetch: refetchKPIs, error: kpisError } = useQuery({
    queryKey: ['overview-kpis', selectedDateRange],
    queryFn: () => {
      if (!selectedDateRange) return Promise.resolve(null);
      
      // CRITICAL: Send actual calendar dates to API, not fiscal_week numbers
      // This allows API to find all retailers with data for these dates,
      // regardless of what fiscal_week number each retailer uses
      console.log('[overview-kpis] Fetching KPIs for calendar dates:', selectedDateRange);
      
      const params = new URLSearchParams();
      params.append('start_date', selectedDateRange.start);
      params.append('end_date', selectedDateRange.end);
      return apiClient.get(`/dashboard/kpis?${params.toString()}`);
    },
    enabled: !!selectedDateRange,
  });
  
  // Get per-retailer breakdown using date range
  const { data: retailerBreakdown, isLoading: breakdownLoading, error: breakdownError } = useQuery({
    queryKey: ['overview-retailer-breakdown', selectedDateRange],
    queryFn: () => {
      if (!selectedDateRange) return Promise.resolve(null);
      
      // CRITICAL: Send actual calendar dates to API, not fiscal_week numbers
      // This matches the KPIs endpoint logic for consistency
      console.log('[retailer-breakdown] Fetching breakdown for calendar dates:', selectedDateRange);
      
      const params = new URLSearchParams();
      params.append('start_date', selectedDateRange.start);
      params.append('end_date', selectedDateRange.end);
      return apiClient.get(`/dashboard/retailer-breakdown?${params.toString()}`);
    },
    enabled: !!selectedDateRange,
  });
  
  // Get trend data for sparklines (last 8 calendar weeks)
  // Derive approximate fiscal week from calendar week number
  const currentCalendarWeek = calendarWeeks?.find(
    w => w.start_date === selectedDateRange?.start && w.end_date === selectedDateRange?.end
  );
  const approxFiscalWeek = currentCalendarWeek
    ? parseInt(currentCalendarWeek.fiscal_year.replace('FY', '')) * 100 + currentCalendarWeek.fiscal_week
    : undefined;

  const { data: trendData } = useQuery({
    queryKey: ['overview-trends', approxFiscalWeek],
    queryFn: () => {
      if (!approxFiscalWeek) return Promise.resolve(null);

      const params = new URLSearchParams();
      params.append('fiscal_week', String(approxFiscalWeek));
      params.append('weeks_back', '8');
      return apiClient.get(`/dashboard/kpi-trends?${params.toString()}`);
    },
    enabled: !!approxFiscalWeek,
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetchKPIs();
    setIsRefreshing(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = kpiCardOrder.indexOf(active.id as KpiCardType);
      const newIndex = kpiCardOrder.indexOf(over.id as KpiCardType);
      
      const newOrder = arrayMove(kpiCardOrder, oldIndex, newIndex);
      updateKpiCardOrder(newOrder);
    }
  };

  const handleExport = () => {
    if (!kpis || !retailerBreakdown || !selectedDateRange) {
      console.warn('Cannot export: missing data');
      return;
    }

    // Find the display label for current date range
    const currentWeek = calendarWeeks?.find(w => 
      w.start_date === selectedDateRange.start && w.end_date === selectedDateRange.end
    );

    exportOverviewToCSV({
      fiscalWeek: currentWeek?.display || `${selectedDateRange.start} to ${selectedDateRange.end}`,
      totalRevenue: kpis.totalRevenue,
      totalUnits: kpis.totalUnits,
      totalSKUs: kpis.activeSKUs,
      totalStores: kpis.storeCount,
      retailers: retailerBreakdown.map((r: any) => ({
        name: r.retailer_name,
        revenue: r.revenue,
        units: r.units,
        skus: r.skus,
        stores: r.stores
      }))
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%`;
  };

  // Prepare retailer breakdown for KPI cards
  const getRetailerBreakdownForMetric = (metric: 'revenue' | 'units' | 'skus' | 'stores') => {
    if (!retailerBreakdown) return [];
    
    return retailerBreakdown
      .filter(r => r.hasData)
      .map(r => ({
        retailer_id: r.retailer_id,
        retailer_name: r.retailer_name,
        value: metric === 'revenue' ? (r.revenue || 0) :
               metric === 'units' ? (r.units || 0) :
               metric === 'skus' ? (r.skus || 0) :
               (r.stores || 0)
      }))
      .filter(r => r.value > 0);
  };

  // Prepare trend data for KPI cards
  const getTrendDataForMetric = (metric: 'revenue' | 'units' | 'skus' | 'stores') => {
    if (!trendData?.weeks) return [];
    
    return trendData.weeks.map(week => ({
      week: week.fiscal_week,
      value: metric === 'revenue' ? week.revenue :
             metric === 'units' ? week.units :
             metric === 'skus' ? week.skus :
             week.stores
    }));
  };

  // Detect anomalies
  const anomalies = React.useMemo(() => {
    if (!kpis || !retailerBreakdown) return [];
    return getAllAnomalies(kpis, retailerBreakdown);
  }, [kpis, retailerBreakdown]);

  // Filter and sort retailers
  const filteredAndSortedRetailers = React.useMemo(() => {
    if (!retailerBreakdown) return [];
    
    // Apply filter
    let filtered = retailerBreakdown;
    if (retailerFilter === 'active') {
      filtered = retailerBreakdown.filter(r => r.hasData);
    } else if (retailerFilter === 'pending') {
      filtered = retailerBreakdown.filter(r => !r.hasData);
    }
    
    // Apply sort
    const sorted = [...filtered].sort((a, b) => {
      switch (retailerSort) {
        case 'revenue':
          return (b.revenue || 0) - (a.revenue || 0);
        case 'units':
          return (b.units || 0) - (a.units || 0);
        case 'skus':
          return (b.skus || 0) - (a.skus || 0);
        case 'alphabetical':
          return a.retailer_name.localeCompare(b.retailer_name);
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [retailerBreakdown, retailerFilter, retailerSort]);

  // Get KPI card data by type
  const getKpiCardData = (type: KpiCardType) => {
    switch (type) {
      case 'revenue':
        return {
          label: 'Total Revenue',
          total: kpis?.totalRevenue || 0,
          icon: DollarSign,
          format: 'currency' as const,
          yoyDeltaText: kpis?.yoyGrowth !== undefined ? `${formatPercentage(kpis.yoyGrowth)} vs LY` : undefined,
          yoyDeltaTone: kpis?.yoyGrowth !== undefined ? (kpis.yoyGrowth >= 0 ? 'success' : 'danger') as const : 'neutral' as const,
          wowDeltaText: kpis?.weekOverWeek?.revenue !== null && kpis?.weekOverWeek?.revenue !== undefined ? `${formatPercentage(kpis.weekOverWeek.revenue)} vs LW` : undefined,
          wowDeltaTone: kpis?.weekOverWeek?.revenue !== null && kpis?.weekOverWeek?.revenue !== undefined ? (kpis.weekOverWeek.revenue >= 0 ? 'success' : 'danger') as const : 'neutral' as const,
          retailerBreakdown: getRetailerBreakdownForMetric('revenue'),
          trendData: getTrendDataForMetric('revenue'),
          trendColor: '#10b981'
        };
      case 'units':
        return {
          label: 'Units Sold',
          total: kpis?.totalUnits || 0,
          icon: ShoppingCart,
          format: 'number' as const,
          yoyDeltaText: kpis?.yoyGrowth !== undefined ? `${formatPercentage(kpis.yoyGrowth)} vs LY` : undefined,
          yoyDeltaTone: kpis?.yoyGrowth !== undefined ? (kpis.yoyGrowth >= 0 ? 'success' : 'danger') as const : 'neutral' as const,
          wowDeltaText: kpis?.weekOverWeek?.units !== null && kpis?.weekOverWeek?.units !== undefined ? `${formatPercentage(kpis.weekOverWeek.units)} vs LW` : undefined,
          wowDeltaTone: kpis?.weekOverWeek?.units !== null && kpis?.weekOverWeek?.units !== undefined ? (kpis.weekOverWeek.units >= 0 ? 'success' : 'danger') as const : 'neutral' as const,
          retailerBreakdown: getRetailerBreakdownForMetric('units'),
          trendData: getTrendDataForMetric('units'),
          trendColor: '#3b82f6'
        };
      case 'skus':
        return {
          label: 'Active SKUs',
          total: kpis?.activeSKUs || 0,
          icon: Package,
          format: 'number' as const,
          wowDeltaText: kpis?.weekOverWeek?.activeSKUs !== null && kpis?.weekOverWeek?.activeSKUs !== undefined ? `${formatPercentage(kpis.weekOverWeek.activeSKUs)} vs LW` : undefined,
          wowDeltaTone: kpis?.weekOverWeek?.activeSKUs !== null && kpis?.weekOverWeek?.activeSKUs !== undefined ? (kpis.weekOverWeek.activeSKUs >= 0 ? 'success' : 'danger') as const : 'neutral' as const,
          retailerBreakdown: getRetailerBreakdownForMetric('skus'),
          trendData: getTrendDataForMetric('skus'),
          trendColor: '#8b5cf6'
        };
      case 'stores':
        return {
          label: 'Total Stores',
          total: kpis?.storeCount || 0,
          icon: Store,
          format: 'number' as const,
          wowDeltaText: kpis?.weekOverWeek?.storeCount !== null && kpis?.weekOverWeek?.storeCount !== undefined ? `${formatPercentage(kpis.weekOverWeek.storeCount)} vs LW` : undefined,
          wowDeltaTone: kpis?.weekOverWeek?.storeCount !== null && kpis?.weekOverWeek?.storeCount !== undefined ? (kpis.weekOverWeek.storeCount >= 0 ? 'success' : 'danger') as const : 'neutral' as const,
          retailerBreakdown: getRetailerBreakdownForMetric('stores'),
          trendData: getTrendDataForMetric('stores'),
          trendColor: '#f59e0b'
        };
    }
  };

  // Sortable KPI Card Wrapper
  const SortableKpiCard = ({ id }: { id: KpiCardType }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };
    
    const cardData = getKpiCardData(id);
    
    return (
      <div ref={setNodeRef} style={style} className="relative">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 z-10 p-1.5 bg-gray-200/80 rounded-md cursor-grab active:cursor-grabbing hover:bg-gray-300/80 transition-colors opacity-0 group-hover:opacity-100"
        >
          <GripVertical className="w-4 h-4 text-gray-600" />
        </div>
        
        <div className="group">
          <OverviewKpiCard {...cardData} />
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen" style={{ background: 'linear-gradient(135deg, #FAFBFC 0%, #F8F9FA 100%)' }}>
      {/* Custom Sidebar for Overview */}
      <OverviewSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        
        {/* Custom Header with Calendar Week Picker */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6 px-8 shadow-lg">
          <div className="max-w-7xl mx-auto space-y-4">
            {/* Title and Actions Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Brand Overview</h1>
                    <p className="text-sm text-indigo-100">Comprehensive insights across all retail partners</p>
                  </div>
                </div>
                
                {/* Retailer Selector - Critical for Navigation */}
                <RetailerSelector showLabel={false} className="bg-white/10 border-white/30 text-white" />
              </div>
              
              {/* Calendar Week Picker and Actions */}
              <div className="flex items-center space-x-3">
                {calendarWeeks && calendarWeeks.length > 0 && (
                  <CalendarWeekPicker
                    weeks={calendarWeeks}
                    selectedRange={selectedDateRange}
                    onSelectRange={setSelectedDateRange}
                    onSelectFiscalWeek={setSelectedFiscalWeek}
                    className="[&_button]:bg-white/10 [&_button]:border-white/30 [&_button]:text-white [&_button]:hover:bg-white/20"
                  />
                )}
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg border border-white/30 transition-all disabled:opacity-50"
                  title="Refresh data"
                >
                  <RotateCcw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={handleExport}
                  disabled={!kpis || !retailerBreakdown}
                  className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg border border-white/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-medium">Export CSV</span>
                </button>
              </div>
            </div>
            
            {/* Stats Row */}
            <div className="flex items-center justify-between text-sm text-indigo-100">
              <div>
                {retailerBreakdown?.filter((r: any) => r.hasData).length || 0} active retailer{(retailerBreakdown?.filter((r: any) => r.hasData).length || 0) !== 1 ? 's' : ''} • 
                {retailerBreakdown?.filter((r: any) => !r.hasData).length || 0} pending data upload
              </div>
              <div>
                👇 Select a retailer card below to view detailed insights
              </div>
            </div>
          </div>
        </div>

        {/* AI Insights Bar - Collapsible, Pulsing, Prominent */}
        <CollapsibleAIInsightsBar />

        {/* Main Content Container */}
        <div className="max-w-7xl mx-auto px-8 py-6 space-y-6">{/* Comfortable spacing for readability */}

          {/* Anomaly Alerts */}
          {anomalies.length > 0 && (
            <AnomalyAlerts anomalies={anomalies} />
          )}

          {/* ========================================= */}
          {/* RETAIL SALES DASHBOARD (B2B2C Sell-Through) */}
          {/* ========================================= */}
          <section className="bg-white border-2 border-blue-200 rounded-2xl overflow-hidden shadow-lg">
            {/* Retail Section Header - Collapsible */}
            <button
              onClick={() => setShowRetailSection(!showRetailSection)}
              className="w-full p-6 hover:bg-blue-50/30 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                  <Store className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-900">Retail Sales Dashboard</h2>
                  <p className="text-sm text-blue-700">
                    B2B2C Sell-Through • What retailers sell to end consumers
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {/* Quick Stats when collapsed */}
                {!showRetailSection && kpis && (
                  <div className="flex items-center space-x-6 mr-4">
                    <div className="text-right">
                      <div className="text-xl font-bold text-gray-900">{formatCurrency(kpis.totalRevenue || 0)}</div>
                      <div className="text-xs text-gray-600">Revenue</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-gray-900">{retailerBreakdown?.filter(r => r.hasData).length || 0}</div>
                      <div className="text-xs text-gray-600">Active Retailers</div>
                    </div>
                  </div>
                )}
                {showRetailSection ? (
                  <ChevronUp className="w-6 h-6 text-gray-600" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-600" />
                )}
              </div>
            </button>

            {/* Retail Section Content */}
            {showRetailSection && (
              <div className="px-6 pb-6">
                {/* Error State */}
                {kpisError && (
                  <div className="bg-white border-2 border-red-200 rounded-2xl p-8 text-center mb-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Data</h3>
                    <p className="text-gray-600 mb-4">There was an error loading the overview metrics</p>
                    <button
                      onClick={handleRefresh}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Portfolio Performance KPIs with Retailer Breakdown */}
                {kpisLoading ? (
                  <div className="mb-6">
                    <div className="mb-6">
                      <div className="flex items-center space-x-3 mb-2">
                        <BarChart3 className="w-6 h-6 text-indigo-600" />
                        <h2 className="text-lg font-bold text-gray-900">Portfolio Performance</h2>
                      </div>
                      <p className="text-gray-600">Aggregated metrics across all retail partners with contribution breakdown</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {[1, 2, 3, 4].map(i => (
                        <OverviewKpiCardSkeleton key={i} />
                      ))}
                    </div>
                  </div>
                ) : !kpisError && (
                  <div className="mb-4">
              {/* Portfolio Performance KPIs - Moved inside Retail Section */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-bold text-gray-900">Portfolio Performance</h3>
                    {kpis?.usedFallback && (
                      <span className="text-[11px] text-gray-400 ml-2 self-end mb-0.5">Based on latest available data per retailer</span>
                    )}
                  </div>
                  {isCustomOrder && (
                    <button
                      onClick={resetKpiCardOrder}
                      className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>Reset</span>
                    </button>
                  )}
                </div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={kpiCardOrder} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {kpiCardOrder.map((cardType) => (
                        <div key={cardType} className="h-full">
                          <SortableKpiCard id={cardType} />
                        </div>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          )}

          {/* Retailer Cards Grid - Enhanced Design */}
          <section>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <Store className="w-6 h-6 text-blue-600" />
                    <h2 className="text-lg font-bold text-gray-900">Retail Partners</h2>
                  </div>
                  <p className="text-gray-600">Click a retailer to access detailed analytics and deep-dive features</p>
                </div>
                {retailerBreakdown && (
                  <div className="text-right">
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-green-600">
                        {retailerBreakdown.filter(r => r.hasData).length}
                      </span> active
                      {retailerBreakdown.filter(r => !r.hasData).length > 0 && (
                        <>
                          {' • '}
                          <span className="font-semibold text-amber-600">
                            {retailerBreakdown.filter(r => !r.hasData).length}
                          </span> pending
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Filter and Sort Controls */}
              {retailerBreakdown && retailerBreakdown.length > 0 && (
                <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center space-x-4">
                    {/* Filter */}
                    <div className="flex items-center space-x-2">
                      <Filter className="w-4 h-4 text-gray-500" />
                      <label className="text-sm text-gray-600 font-medium">Show:</label>
                      <select
                        value={retailerFilter}
                        onChange={(e) => setRetailerFilter(e.target.value as 'all' | 'active' | 'pending')}
                        className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="all">All Retailers</option>
                        <option value="active">Active Only</option>
                        <option value="pending">Pending Only</option>
                      </select>
                    </div>

                    {/* Sort */}
                    <div className="flex items-center space-x-2">
                      <ArrowUpDown className="w-4 h-4 text-gray-500" />
                      <label className="text-sm text-gray-600 font-medium">Sort by:</label>
                      <select
                        value={retailerSort}
                        onChange={(e) => setRetailerSort(e.target.value as 'revenue' | 'units' | 'skus' | 'alphabetical')}
                        className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="revenue">Revenue (High to Low)</option>
                        <option value="units">Units (High to Low)</option>
                        <option value="skus">SKUs (High to Low)</option>
                        <option value="alphabetical">Alphabetical</option>
                      </select>
                    </div>
                  </div>

                  {/* Count badge */}
                  <div className="text-sm text-gray-600">
                    Showing <span className="font-semibold text-indigo-600">{filteredAndSortedRetailers.length}</span> of {retailerBreakdown.length} retailers
                  </div>
                </div>
              )}
            </div>

            {breakdownLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <RetailerCardSkeleton key={i} />
                ))}
              </div>
            ) : breakdownError ? (
              <div className="bg-white border-2 border-amber-200 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Store className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Retailers</h3>
                <p className="text-gray-600">There was an error loading retailer information</p>
              </div>
            ) : filteredAndSortedRetailers && filteredAndSortedRetailers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAndSortedRetailers.map((retailer) => (
                  <RetailerCard
                    key={retailer.retailer_id}
                    retailerId={retailer.retailer_id}
                    revenue={retailer.revenue}
                    units={retailer.units}
                    skuCount={retailer.skus}
                    storeCount={retailer.stores}
                    hasData={retailer.hasData}
                    revenueLwPct={retailer.revenue_lw_pct}
                    revenueLyPct={retailer.revenue_ly_pct}
                    dataAsOf={retailer.dataAsOf}
                  />
                ))}
              </div>
            ) : retailerBreakdown && retailerBreakdown.length > 0 ? (
              <NoMatchesEmpty 
                onAction={() => { setRetailerFilter('all'); setRetailerSort('revenue'); }}
              />
            ) : (
              <NoRetailersEmpty />
            )}
          </section>

          {/* Revenue Trends Chart */}
          {trendData && trendData.weeks && trendData.weeks.length > 0 && (
            <div className="mb-6">
                    <RetailerGrowthTrends 
                      trendData={trendData.weeks}
                      activeRetailers={retailerBreakdown?.filter(r => r.hasData).map(r => ({
                        retailer_id: r.retailer_id,
                        retailer_name: r.retailer_name
                      })) || []}
                    />
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ========================================= */}
          {/* DTC SALES DASHBOARD (B2C Direct Sales) */}
          {/* ========================================= */}
          {selectedDateRange && (
            <DtcOverviewSection 
              startDate={selectedDateRange.start}
              endDate={selectedDateRange.end}
            />
          )}

        </div>
      </div>
    </div>
  );
}

// Helper to get retailer chart color
function getRetailerColor(slug: string): string {
  const colors: Record<string, string> = {
    'ulta': '#FF6B35',
    'jcp': '#CC0000',
    'sephora': '#4A4A4A',
    'macys': '#E21B23',
  };
  return colors[slug] || '#3B82F6';
}

